import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import React, { useState } from 'react';
import { Waypoint } from 'react-waypoint';
import PublicSearchModal from './PublicSearchModal';
import * as styles from './PublicSidebar.module.scss';
import PageLayout from '../app/layout/PageLayout';
import { useSidebarOpenState } from '../app/layout/SidebarOpenStateContext';
import { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import HotkeyInfo, { SHORT_KEY } from '../components/HotkeyInfo';
import IconButton from '../components/IconButton';
import SVGIcon from '../components/SVGIcon';
import { gql } from '../graphql/generated';
import searchIcon from '../icons/non-prefilled/16x16/search.svg';
import sidebarToggleIcon from '../icons/non-prefilled/16x16/sidebarToggle.svg';
import topicIcon from '../icons/prefilled/16x16/topic.svg';
import { getPostIcon } from '../post/PostIcon';
import SidebarRow from '../sidebar/SidebarRow';
import SidebarSection from '../sidebar/SidebarSection';
import { getSidebarSectionNameWithFallback } from '../sidebar/SidebarSectionsProvider';
import { UIActions } from '../ui/uiSlice';
import { publicPostPath } from '../utils/url/postPath';
import { publicTopicPath } from '../utils/url/topicPath';
import useShortcut from '../utils/useShortcut';

interface SidebarToggleButtonProps {
  floating?: boolean;
}

const SidebarToggleButton = ({ floating }: SidebarToggleButtonProps) => {
  const { toggleSidebar } = useSidebarOpenState();

  return (
    <div
      className={classNames(styles.sidebarToggleButtonContainer, {
        [styles.isFloating]: floating,
      })}
    >
      <IconButton
        onClick={() => {
          toggleSidebar();
        }}
        aria-label="Toggle toolbar"
        className={styles.sidebarToggleButton}
      >
        <SVGIcon size="large">{sidebarToggleIcon}</SVGIcon>
      </IconButton>
    </div>
  );
};

const SECTION_QUERY = gql(/** @complexity 7 */ `
  query PublicSidebarSectionQuery($host: String!) {
    organization(host: $host) {
      id
      sections {
        id
        name
        items {
          ... on PublicPost {
            id
            title
            ...getPostIcon_post
            ...publicPostPath_post
          }
          ... on PublicTopic {
            id
            name
          }
        }
      }
    }
  }
`);

const PublicSidebar = () => {
  const { isSidebarOpen } = useSidebarOpenState();

  const [isScrolled, setIsScrolled] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const { data } = useQuery(SECTION_QUERY, {
    variables: { host: window.__slab.PUBLIC_ORG.effectiveHost },
  });

  const publicSections = data?.organization.sections;
  const dispatch = useAppDispatch();

  const openSearchModal = () => {
    dispatch(UIActions.openModal(PublicSearchModal, { alignToTop: true }));
  };

  useShortcut('mod+k', (e) => {
    e.preventDefault();
    openSearchModal();
  });

  return (
    <>
      <PageLayout.Sidebar
        publicVariant
        className={classNames(styles.container, {
          [styles.isScrolled]: isScrolled,
          [styles.isAtBottom]: isAtBottom,
        })}
      >
        <div className={styles.searchBarContainer}>
          <Button
            icon={<SVGIcon className={styles.searchIcon}>{searchIcon}</SVGIcon>}
            className={styles.searchBar}
            onClick={() => {
              openSearchModal();
            }}
          >
            Search
          </Button>
          <div className={styles.shortcut}>
            <HotkeyInfo keys={new Set([SHORT_KEY])} />
            <HotkeyInfo keys={new Set(['K'])} />
          </div>
        </div>
        {publicSections && (
          <div className={styles.sections}>
            <Waypoint
              onEnter={() => {
                setIsScrolled(false);
              }}
              onLeave={() => {
                setIsScrolled(true);
              }}
            />
            {publicSections.map((section) => {
              return (
                <SidebarSection
                  publicVariant
                  key={section.id}
                  id={section.id}
                  title={getSidebarSectionNameWithFallback(section.name)}
                  hasChildren={section.items.length > 0}
                >
                  {section.items.map((item) => {
                    if (item.__typename === 'PublicTopic') {
                      return (
                        <SidebarRow
                          key={item.id}
                          icon={topicIcon}
                          to={publicTopicPath(item)}
                        >
                          {item.name}
                        </SidebarRow>
                      );
                    }
                    if (item.__typename === 'PublicPost') {
                      return (
                        <SidebarRow
                          key={item.id}
                          icon={getPostIcon(item, true)}
                          to={publicPostPath(item)}
                        >
                          {item.title}
                        </SidebarRow>
                      );
                    }
                    return null;
                  })}
                </SidebarSection>
              );
            })}
            <Waypoint
              onEnter={() => {
                setIsAtBottom(true);
              }}
              onLeave={() => {
                setIsAtBottom(false);
              }}
            />
          </div>
        )}
        <SidebarToggleButton />
      </PageLayout.Sidebar>
      {!isSidebarOpen && <SidebarToggleButton floating />}
    </>
  );
};

export default PublicSidebar;
